import { Box, Flex, Input, Link, Text, VStack } from "@chakra-ui/react"
import LazyRedactorEditor from "@pathwright/ui/src/components/richtext/LazyRedactorEditor"
import UserAvatar from "@pathwright/ux/src/components/avatar/UserAvatar"
import DiscardButton from "@pathwright/ux/src/components/form/DiscardButton"
import Field from "@pathwright/ux/src/components/form/Field"
import SubmitButton from "@pathwright/ux/src/components/form/SubmitButton"
import { FormikWithDiscardFormState } from "@pathwright/ux/src/components/form/state/FormikDiscardFormState"
import KeyboardFormSubmit from "@pathwright/ux/src/components/form/state/KeyboardFormSubmit"
import { useTranslate } from "@pathwright/ux/src/components/lng/useTranslate"
import { Form, FormikProps } from "formik"
import { useEffect, useState } from "react"
import { z } from "zod"
import { toFormikValidate } from "zod-formik-adapter"
import {
  DiscussionContextInput,
  DiscussionPostFragment,
  DiscussionResponseFragment
} from "@pathwright/graphql-client/src/generated"
import { usePathwrightContext } from "../../pathwright/PathwrightContext"
import {
  NonArchivedDiscussionResponseFragment,
  useDiscussionContext
} from "../DiscussionProvider"
import useDiscussionResponseHasUnsavedChanges from "./useDiscussionResponseHasUnsavedChanges"

const discussionResponseSchema = z.object({
  body: z.string().min(1, "Required")
})

const validate = toFormikValidate(discussionResponseSchema)

type DiscussionResponseValues = z.infer<typeof discussionResponseSchema>

// Scopes for state storage to a discussion or context.
// Deriving the context from the discussion or passed context.
export function getDiscussionResponseFormStateKey({
  discussion,
  response,
  responseParent
}: {
  discussion: DiscussionPostFragment
  response?: DiscussionResponseFragment | null
  responseParent?: DiscussionResponseFragment | null
}): [
  "response",
  number | null,
  DiscussionContextInput & {
    discussion_id: number
    response_parent_id?: number | null
  }
] {
  const context = {
    discussion_id: discussion.id,
    response_parent_id: responseParent?.id,
    resource_id: discussion.resource_id,
    cohort_id: discussion.cohort_id,
    step_id: discussion.step_id
  }

  return ["response", response?.id || null, context]
}

// Renders a text input istead of full form. User clicks to expand to full form.
export function DiscussionResponseFauxForm() {
  const { t } = useTranslate()
  const { me } = usePathwrightContext()
  const [showForm, setshowForm] = useState(false)

  // Check for unsaved response form state, automatically showing full form if so.
  const hasUnsavedResponse = useDiscussionResponseHasUnsavedChanges()

  useEffect(() => {
    // Only auto-show form if there are unsaved changes. Note that we don't want to hide the form
    // when unsaved changes are cleared.
    if (hasUnsavedResponse) setshowForm(true)
  }, [hasUnsavedResponse])

  // Auto-show form when intent=reply is in query params.
  useEffect(() => {
    // Handle intent query param for auto-opening the reponse form.
    const searchParams = new URLSearchParams(window.location.search)
    if (searchParams.get("intent") === "reply") {
      // Small delay to avoid breaking panel animation
      setTimeout(() => {
        handleshowForm()
      }, 100)
    }
  }, [])

  const handleshowForm = () => {
    setshowForm(true)
  }

  return showForm ? (
    <DiscussionResponseForm onCancel={() => setshowForm(false)} />
  ) : (
    <Flex gap={3} align="center">
      {/* User avatar */}
      <UserAvatar size={["xs", "sm"]} user={me} />
      <Input
        type="text"
        value={t("discussion.responses.form.answer_placeholder")}
        className="form-control fake-input"
        onClick={handleshowForm}
        readOnly
        cursor="text"
        bg="white"
        _hover={{ borderColor: "blue.400" }}
      />
    </Flex>
  )
}

type DiscussionResponseFormProps = {
  response?: NonArchivedDiscussionResponseFragment | null
  replyParent?: NonArchivedDiscussionResponseFragment
  onCancel?: () => void
}

function DiscussionResponseForm({
  response,
  replyParent,
  onCancel
}: DiscussionResponseFormProps) {
  const { t } = useTranslate()
  const post = useDiscussionContext((c) => c.post)
  const createResponse = useDiscussionContext((c) => c.createResponse)
  const updateResponse = useDiscussionContext((c) => c.updateResponse)
  const setReplyingTo = useDiscussionContext((c) => c.setReplyingTo)
  const setEditingResponse = useDiscussionContext((c) => c.setEditingResponse)
  const { me } = usePathwrightContext()

  const isNew = !response?.id

  // Determine response noun and labels.
  // TODO: we should use designated translation keys for each of these, not a concatenation.
  const actionLabel = isNew
    ? replyParent
      ? `${t("discussion.responses.reply")} ${t("discussion.responses.to")} ${
          replyParent.user.first_name
        }`
      : t("discussion.responses.reply")
    : `${t("discussion.responses.update")} ${t("discussion.responses.reply")}`

  const placeholder = `${actionLabel}...`

  // Resets all edit/create intent.
  function reset() {
    setReplyingTo(null)
    setEditingResponse(null)
    onCancel?.()
  }

  const handleSubmit = async (responseFormValues: DiscussionResponseValues) => {
    // For new responses
    if (isNew) {
      // Not awaiting result, relying on optimistic UI update.
      createResponse({
        body: responseFormValues.body.trim(),
        // Attachments are used in single-depth (Feedback) discussion, but not currently in threaded discussion.
        // attachments
        depth: response?.depth || (replyParent?.depth || 0) + 1,
        discussion_id: post!.id,
        reply_parent_id: replyParent?.id
      })
    } else {
      // Not awaiting result, relying on optimistic UI update.
      updateResponse({
        ...response,
        body: responseFormValues.body.trim()
      })
    }

    reset()
  }

  const formStateKey = getDiscussionResponseFormStateKey({
    discussion: post!,
    response,
    responseParent: replyParent
  })

  return (
    <Box
      className="discussion-response-form-view"
      w="full"
      css={{
        "& .redactor-box": { mb: 0 },
        // Reducing from overridden 9999 value, ensures discussion action icon tooltips appear above the toolbar.
        "& .redactor-toolbar": { zIndex: "100 !important" }
      }}
    >
      <Flex gap={3} align="flex-start">
        {/* User avatar */}
        <UserAvatar size={["xs", "sm"]} user={me} />

        <VStack flex={1} align="stretch" gap={3} minW={0}>
          <FormikWithDiscardFormState
            formStateKey={formStateKey}
            initialValues={{
              body: response?.body || ""
            }}
            onSubmit={handleSubmit}
            validate={validate}
            formStateOnSubmit="clearOptimistic"
          >
            {(form: FormikProps<DiscussionResponseValues>) => (
              <Form className="reponse-form-input response-body-text">
                <KeyboardFormSubmit />
                <VStack
                  gap={3}
                  align="stretch"
                  css={{
                    "& .redactor-editor-textarea": {
                      display: "inline-block",
                      width: "100%"
                    }
                  }}
                >
                  {/* Response body */}
                  <Field
                    name="body"
                    as={LazyRedactorEditor}
                    // Override value passed by Formik to avoid slower updates due to controlled component.
                    // Likely need to revisit Form State Storage for efficiency (happens with the Input field as well).
                    value={undefined}
                    initialValue={form.values.body}
                    onChange={(body: string) =>
                      form.setFieldValue("body", body)
                    }
                    onBlur={() => form.setFieldTouched("body", true)}
                    placeholder={placeholder}
                    autoFocus
                    minH="120px"
                    bg="white"
                    toolbarOverflow={false}
                  />

                  {/* Action buttons */}
                  <Flex gap={2} align="center">
                    <SubmitButton
                      type="submit"
                      colorPalette="brand"
                      size="md"
                      w="auto"
                    >
                      {actionLabel}
                    </SubmitButton>

                    <Text
                      fontSize="sm"
                      color="gray.500"
                      className="pw-form-cancel"
                    >
                      {t("discussion.form.tech_question.or")}{" "}
                      <Link asChild variant="underline">
                        <DiscardButton
                          colorPalette="brand"
                          variant="plain"
                          onClick={reset}
                          ignoreDirty
                          className="cancel-response"
                          p={0}
                          w="min-content"
                          display="inline-block"
                        />
                      </Link>
                    </Text>
                  </Flex>
                </VStack>
              </Form>
            )}
          </FormikWithDiscardFormState>
        </VStack>
      </Flex>
    </Box>
  )
}

export default DiscussionResponseForm
